import type { ImageResource } from "models/types/agency-settings";
import { AppLayout } from "components/app-layout";
import { PortalErrorScreen } from "components/portal-error-screen";
import { AgencySettingsProvider } from "contexts/agency-settings-provider";
import { RequireAuthRole } from "contexts/auth-provider";
import { LandlordSettingsProvider } from "contexts/landlord-settings-provider";
import type { SessionResponse } from "contexts/session";
import type { Route } from "lib/react-router-dom";
import { LeftNavigation } from "modules/landlord-portal/components/left-navigation";
import { LANDLORD_PORTAL_PATHS } from "modules/landlord-portal/paths";
import { FinancialsScreen } from "modules/landlord-portal/screens/financial/financials-screen";
import { MaintenanceScreen } from "modules/landlord-portal/screens/maintenance/maintenance-screen";
import { OverviewScreen } from "modules/landlord-portal/screens/overview/overview-screen";
import { type OwnershipSelectorData } from "types/landlord-ownership-selector";
import type { LandlordSettingsLoaderData } from "types/landlord-settings-loader-data";
import { apiClient } from "utils/api-client";
import { isTokenExpired } from "utils/auth";

export const LANDLORD_PORTAL_ROUTES: Route[] = [
  {
    id: "landlord-root",
    path: LANDLORD_PORTAL_PATHS.DEFAULT,
    handle: {
      title: "Landlord",
    },
    loader: async (): Promise<LandlordSettingsLoaderData> => {
      if (isTokenExpired()) {
        throw new Response(
          JSON.stringify({
            type: "token_expired",
            title: "Link Expired",
            message: "Your access link has expired.",
          }),
          { status: 401, headers: { "Content-Type": "application/json" } },
        );
      }

      const ownerships = await apiClient.get<OwnershipSelectorData>(
        "/landlord-portal/ownerships",
      );

      if (ownerships.data.length === 0) {
        throw new Response(
          JSON.stringify({
            title: "Landlord does not have ownerships.",
            message: "No ownerships found",
          }),
          { status: 404, headers: { "Content-Type": "application/json" } },
        );
      }

      // The landlord agency logo/name lives on the session silo.
      const { data: user } = await apiClient.get<SessionResponse>("/user");
      const siloLogo = user.silo?.logo;

      const primaryLogo: ImageResource | null = siloLogo
        ? {
            id: "external",
            path: siloLogo,
            name: user.silo?.display_name ?? "",
            size: 0,
            mime_type: "image/png",
            width: 0,
            height: 0,
            scale: 1,
            urls: {
              "log.primary": siloLogo,
              "log.secondary": siloLogo,
            },
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }
        : null;

      return {
        agencySettings: Promise.resolve({
          branding: {
            agency_name: user.silo?.display_name ?? null,
            primary_logo: primaryLogo,
            secondary_logo: null,
            accent_color: "",
            navigation_color: "",
            heading_font: null,
            heading_font_family: null,
            body_font: null,
            body_font_family: null,
          },
        }),
        ownerships,
      };
    },
    errorElement: <PortalErrorScreen />,
    element: (
      <RequireAuthRole anyOf={["landlord", "agent", "agent_manager"]}>
        <AgencySettingsProvider>
          <LandlordSettingsProvider>
            <AppLayout leftNavigationMenu={<LeftNavigation />} />
          </LandlordSettingsProvider>
        </AgencySettingsProvider>
      </RequireAuthRole>
    ),
    children: [
      {
        index: true,
        Component: OverviewScreen,
        handle: {
          title: "Overview",
        },
      },
      {
        path: LANDLORD_PORTAL_PATHS.FINANCIALS,
        Component: FinancialsScreen,
        handle: {
          title: "Financials",
        },
      },
      {
        path: LANDLORD_PORTAL_PATHS.MAINTENANCE,
        Component: MaintenanceScreen,
        handle: {
          title: "Maintenance",
        },
      },
    ],
  },
];

/**
 * TODO: the remaining children list routes for landlord-portal
 *       WIP if this will be included
 *
 * export const LANDLORD_PORTAL_ROUTES: Route[] = [
 *     children: [
 *       {
 *         path: LANDLORD_PORTAL_PATHS.MAINTENANCE,
 *         Component: MaintenanceScreen,
 *         handle: {
 *           title: "Maintenance",
 *         },
 *       },
 *       {
 *         path: LANDLORD_PORTAL_PATHS.INSPECTIONS,
 *         Component: InspectionsScreen,
 *         handle: {
 *           title: "Inspections",
 *         },
 *       },
 *       {
 *         path: LANDLORD_PORTAL_PATHS.INSPECTION_DETAILS,
 *         Component: InspectionDetailsScreen,
 *         handle: {
 *           title: "Inspection Details Screen",
 *         },
 *       },
 *       {
 *         path: LANDLORD_PORTAL_PATHS.DOCUMENTS,
 *         Component: DocumentsScreen,
 *         handle: {
 *           title: "Documents",
 *         },
 *       },
 *     ],
 *   },
 * ];
 */
